//#region src/engine.ts
/**
* Rule Engine - applies rules to objects
*
* Note: T cannot have fields named 'and', 'or', or 'not' as they are reserved
* for logical operators. T must only contain primitive values (string, number, boolean)
* or arrays of primitives.
*/
var RuleEngine = class {
	constructor(rules, defaultRule) {
		this.rules = rules;
		this.defaultRule = defaultRule;
	}
	/**
	* Apply all matching rules to an object
	*/
	apply(item) {
		let result = this.defaultRule ? {
			...item,
			...this.defaultRule
		} : { ...item };
		for (const rule of this.rules) if (this.matches(item, rule.when)) {
			if (rule.set) result = {
				...result,
				...rule.set
			};
			if (rule.push) for (const key in rule.push) {
				const pushValue = rule.push[key];
				const currentValue = result[key];
				if (Array.isArray(currentValue) && Array.isArray(pushValue)) result = {
					...result,
					[key]: [...currentValue, ...pushValue]
				};
			}
		}
		return result;
	}
	/**
	* Apply rules to multiple objects
	*/
	applyAll(items) {
		return items.map((item) => this.apply(item));
	}
	/**
	* Check if an object matches a condition
	*/
	matches(item, condition) {
		if ("and" in condition && Array.isArray(condition.and)) return condition.and.every((c) => this.matches(item, c));
		if ("or" in condition && Array.isArray(condition.or)) return condition.or.some((c) => this.matches(item, c));
		if ("not" in condition && condition.not) return !this.matches(item, condition.not);
		const fieldCondition = condition;
		for (const key in fieldCondition) {
			const operator = fieldCondition[key];
			const value = item[key];
			if (!this.matchesOperator(value, operator)) return false;
		}
		return true;
	}
	/**
	* Check if a value matches a pattern (regex or exact match)
	*/
	matchesPattern(value, pattern) {
		if (pattern instanceof RegExp) return pattern.test(String(value));
		return value === pattern;
	}
	/**
	* Normalize shortcut syntax to full operator format
	*/
	normalizeOperator(operator) {
		if (!operator) return null;
		if (typeof operator === "object" && !Array.isArray(operator) && !(operator instanceof RegExp)) return operator;
		if (operator instanceof RegExp) return { match: operator };
		if (Array.isArray(operator)) return { in: operator };
		return { eq: operator };
	}
	/**
	* Check if a value matches an operator
	*/
	matchesOperator(value, operator) {
		const normalized = this.normalizeOperator(operator);
		if (!normalized) return true;
		const op = normalized;
		if ("eq" in op) return value === op["eq"];
		if ("in" in op && Array.isArray(op["in"])) {
			const inArray = op["in"];
			if (Array.isArray(value)) return value.some((v) => inArray.some((pattern) => this.matchesPattern(v, pattern)));
			return inArray.some((pattern) => this.matchesPattern(value, pattern));
		}
		if ("match" in op) {
			const matchValue = op["match"];
			return (Array.isArray(matchValue) ? matchValue : [matchValue]).some((pattern) => {
				if (pattern instanceof RegExp) return pattern.test(String(value));
				const regexPattern = String(pattern).replace(/\*/g, "___GLOB_STAR___").replace(/\?/g, "___GLOB_QUESTION___").replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/___GLOB_STAR___/g, ".*").replace(/___GLOB_QUESTION___/g, ".");
				return (/* @__PURE__ */ new RegExp(`^${regexPattern}$`)).test(String(value));
			});
		}
		return true;
	}
};

//#endregion
export { RuleEngine };
//# sourceMappingURL=index.js.map